import React, { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
  Input,
  Badge,
  Card,
  CardBody
} from "reactstrap"
import { post } from "../../../../helpers/api_helper"
import { API_URL } from "../../../../helpers/url_helper"
import {
  formatCurrency,
  formatDateTime,
  getInvoiceAnalyticsDetails,
  exportInvoiceAnalyticsDetails
} from "../../../../helpers/rfq_dashboard_api"

const FinancialDetailModal = ({ 
  isOpen, 
  toggle, 
  type, 
  title,
  filters = {},
  additionalData = {} 
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [exporting, setExporting] = useState(false)

  // Reset pagination and load data when modal opens or type changes
  useEffect(() => {
    if (isOpen && type) {
      console.log("Modal opened with type:", type)
      console.log("Modal filters:", filters)
      // Reset pagination state when modal opens or type changes
      setCurrentPage(1)
      setSearchTerm("")
      loadData(1)
    }
  }, [isOpen, type, perPage, filters])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all state when modal closes
      setCurrentPage(1)
      setSearchTerm("")
      setData([])
      setPagination(null)
      setLoading(false)
      setError(null)
    }
  }, [isOpen])

  // Load data from API
  const loadData = async (page = 1) => {
    try {
      setLoading(true)
      setError(null)
      setCurrentPage(page)

      const requestData = {
        ...filters,
        type: type,
        page: page,
        per_page: perPage,
        search: searchTerm,
        ...additionalData
      }

      console.log("Loading financial detail data:", requestData)

      // First test the endpoint
      try {
        const testResponse = await post('/api/test-financial-details', { test: true })
        console.log("Test endpoint response:", testResponse)
      } catch (testError) {
        console.error("Test endpoint failed:", testError)
      }

      // Use dedicated Invoice Analytics API for invoice_analytics type
      let response
      if (type === 'invoice_analytics') {
        response = await getInvoiceAnalyticsDetails(requestData)
      } else {
        response = await post('/api/rfq-financial-details', requestData)
      }
      
      if (response && response.status) {
        setData(response.data.records || [])
        setPagination(response.data.pagination || null)
        console.log("Financial detail data loaded:", response.data)
      } else {
        throw new Error(response?.message || "Failed to load financial details")
      }
    } catch (err) {
      console.error("Error loading financial details:", err)

      // Show mock data for testing
      console.log("Using mock data for testing...")
      const mockData = [
        {
          id: 1,
          rfq_number: 'RFQ_000001',
          title: 'Sample RFQ 1',
          project_name: 'Demo Project',
          vendor_name: 'Test Vendor',
          status: 'Active',
          amount: 50000,
          budget: 55000,
          final_amount: 50000,
          paid_amount: 30000,
          pending_amount: 20000,
          date: new Date().toISOString(),
          created_at: new Date().toISOString(),
          estimated_date: '15/01/25', // dd/mm/yy format
          completion_date: null
        },
        {
          id: 2,
          rfq_number: 'RFQ_000002',
          title: 'Sample RFQ 2',
          project_name: 'Demo Project 2',
          vendor_name: 'Test Vendor 2',
          status: 'Pending',
          amount: 75000,
          budget: 80000,
          final_amount: 75000,
          paid_amount: 0,
          pending_amount: 75000,
          date: new Date().toISOString(),
          created_at: new Date().toISOString(),
          estimated_date: '20/02/25', // dd/mm/yy format
          completion_date: '10/12/24' // dd/mm/yy format
        }
      ]

      setData(mockData)
      setPagination({
        current_page: 1,
        per_page: perPage,
        total: mockData.length,
        total_pages: Math.ceil(mockData.length / perPage),
        has_next: Math.ceil(mockData.length / perPage) > 1,
        has_prev: false
      })

      setError(`API Error: ${err.message || "Failed to load financial details"}. Showing mock data for testing.`)
    } finally {
      setLoading(false)
    }
  }

  // Handle search with debouncing
  useEffect(() => {
    if (!isOpen) return

    const timeoutId = setTimeout(() => {
      if (searchTerm !== undefined) { // Only search if searchTerm has been set
        setCurrentPage(1)
        loadData(1)
      }
    }, 500) // 500ms debounce

    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  // Handle search button click
  const handleSearch = () => {
    setCurrentPage(1)
    loadData(1)
  }

  // Handle CSV export
  const handleExportCSV = async () => {
    try {
      setExporting(true)
      setError(null)

      const requestData = {
        ...filters,
        type: type,
        search: searchTerm,
        ...additionalData
      }

      console.log("Exporting financial detail data:", requestData)

      // Use dedicated Invoice Analytics export API for invoice_analytics type
      const exportEndpoint = type === 'invoice_analytics'
        ? '/api/rfq-export-invoice-analytics-details'
        : '/api/rfq-export-financial-details'

      // Use the post helper with blob response type (same pattern as other working exports)
      const response = await post(exportEndpoint, requestData, {
        responseType: 'blob',
        headers: {
          'Accept': 'text/csv'
        }
      })

      console.log("Financial details export response:", response)

      // Handle response - check if it's a blob or wrapped in data
      let blob
      if (response instanceof Blob) {
        blob = response
      } else if (response.data instanceof Blob) {
        blob = response.data
      } else {
        throw new Error("Export returned unexpected data format")
      }

      // Generate filename
      const filename = `financial_${type}_${new Date().toISOString().slice(0, 10)}.csv`

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      console.log("Financial details CSV export completed")
    } catch (err) {
      console.error("Export error:", err)
      setError(err.message || "Failed to export data")
    } finally {
      setExporting(false)
    }
  }

  // Format date as dd-mm-yyyy
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'

    // If the date is already in dd-mm-yyyy format from backend, return as is
    if (typeof dateString === 'string' && dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
      return dateString
    }

    try {
      const date = new Date(dateString)

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date'
      }

      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}-${month}-${year}`
    } catch (error) {
      console.error('Date formatting error:', error, 'Input:', dateString)
      return 'Invalid Date'
    }
  }

  // Get status value based on modal type
  const getStatusValue = (record) => {
    if (type === 'total_budget' || type === 'total_final_amount') {
      return record.status || 'Unknown'
    } else if (type === 'total_paid' || type === 'pending_amount' || type === 'advance_payment') {
      return record.payment_status || 'Unknown'
    }
    return record.status || 'Unknown'
  }

  // Get modal title based on type
  const getModalTitle = () => {
    if (title) return title

    const titles = {
      'total_budget': 'Total Budget Details',
      'total_final_amount': 'Final Amount Details',
      'total_paid': 'Total Paid Amount Details',
      'total_savings': 'Total Savings Details',
      'pending_amount': 'Pending Amount Details',
      'advance_payment': 'Advance Payment Details',
      'payment_trends': 'Payment Trends Details',
      'invoice_analytics': 'Invoice Analytics Details',
      'invoice_amount': 'Invoice Amount Details',
      'gst_amount': 'GST Amount Details',
      'financial_metrics': 'Financial Metrics Details'
    }

    return titles[type] || 'Financial Details'
  }

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'completed':
        return 'success'
      case 'pending':
      case 'in_progress':
        return 'warning'
      case 'overdue':
      case 'cancelled':
        return 'danger'
      case 'draft':
        return 'secondary'
      default:
        return 'primary'
    }
  }

  console.log("FinancialDetailModal render - type:", type)
  console.log("FinancialDetailModal render - data:", data)
  console.log("FinancialDetailModal render - searchTerm:", searchTerm)

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="financial-detail-modal">
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center">
          <i className="bx bx-money me-2"></i>
          {getModalTitle()}
          {pagination && (
            <Badge color="primary" className="ms-2">
              {pagination.total} Records Found
            </Badge>
          )}
          {!pagination && loading && (
            <Badge color="secondary" className="ms-2">
              Loading...
            </Badge>
          )}
        </div>
      </ModalHeader>
      <ModalBody>
        {/* Header with Search, Per-Page Selection, and Export */}
        <Row className="mb-3">
          <Col md={6}>
            <Input
              type="text"
              placeholder="Search by RFQ#, title, project, vendor, or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading}
            />
          </Col>
          <Col md={2}></Col>
          <Col md={2}>
            <div className="d-flex align-items-center">
              <span className="me-2 text-nowrap">Show:</span>
              <Input
                type="select"
                value={perPage}
                onChange={(e) => {
                  setPerPage(parseInt(e.target.value))
                  loadData(1) // Reload with new per page value
                }}
                disabled={loading}
                style={{fontSize: '0.875rem'}}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>
            </div>
          </Col>
          <Col md={2} className="text-end">
            <Button
              color="success"
              onClick={handleExportCSV}
              disabled={exporting || loading}
              block
            >
              {exporting ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Exporting...
                </>
              ) : (
                <>
                  <i className="bx bx-download me-1"></i>
                  Export CSV
                </>
              )}
            </Button>
          </Col>
        </Row>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <div className="mt-2">Loading financial data...</div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Alert color="danger" className="mb-3">
            <i className="bx bx-error-circle me-2"></i>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        {!loading && !error && (
          <>
            {data.length > 0 ? (
              <div className="table-responsive">
                <Table striped hover>
                  <thead>
                    <tr>
                      {type === 'invoice_analytics' ? (
                        <>
                          <th>RFQ#</th>
                          <th>RFQ Title</th>
                          <th>Project Name</th>
                          <th>Vendor Name</th>
                          <th>Taxable Amount</th>
                          <th>GST Amount</th>
                          <th>Total Invoice Amount</th>
                          <th>Invoice No</th>
                          <th>Invoice Date</th>
                          <th>Completion Date</th>
                          <th>Status</th>
                        </>
                      ) : (
                        <>
                          <th>RFQ#</th>
                          <th>RFQ Title</th>
                          <th>Project Name</th>
                          <th>Vendor Name</th>
                          {type === 'financial_summary_all' ? (
                            <>
                              <th>Budget Amount</th>
                              <th>Final Amount</th>
                              <th>Paid Amount</th>
                              <th>Pending Amount</th>
                            </>
                          ) : (type === 'total_budget' || type === 'total_final_amount') ? (
                            <>
                              <th>Budget Amount</th>
                              <th>Final Amount</th>
                            </>
                          ) : (
                            <th>{type === 'total_paid' ? 'Payment Amount' : type === 'pending_amount' ? 'Pending Amount' : 'Amount'}</th>
                          )}
                          <th>Status</th>
                          {type === 'financial_summary_all' ? (
                            <>
                              <th style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>Created Date</th>
                              <th style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>Estimated Date</th>
                              <th style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>Completion Date</th>
                            </>
                          ) : (
                            <>
                              <th style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>{type === 'total_paid' ? 'Payment Date' : 'Created Date'}</th>
                              <th style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>Estimated Date</th>
                              <th style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>Completion Date</th>
                            </>
                          )}
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((record, index) => (
                      <tr key={record.id || index}>
                        {type === 'invoice_analytics' ? (
                          <>
                            <td>
                              <strong className="text-primary">{record.rfq_number || '-'}</strong>
                            </td>
                            <td>
                              <span className="text-dark">{record.title || '-'}</span>
                            </td>
                            <td>
                              <span className="text-muted">{record.project_name || 'Unknown Project'}</span>
                            </td>
                            <td>
                              <span className="text-muted">{record.vendor_name || 'Not Assigned'}</span>
                            </td>
                            <td>
                              <strong className="text-success">{formatCurrency(record.taxable_amount || 0)}</strong>
                            </td>
                            <td>
                              <strong className="text-warning">{formatCurrency(record.gst_amount || 0)}</strong>
                            </td>
                            <td>
                              <strong className="text-info">{formatCurrency(record.total_invoice_amount || 0)}</strong>
                            </td>
                            <td>
                              <span className="text-muted">{record.invoice_number || '-'}</span>
                            </td>
                            <td>
                              <span className="text-muted">{formatDate(record.invoice_date)}</span>
                            </td>
                            <td>
                              <span className="text-muted">{formatDate(record.completion_date)}</span>
                            </td>
                            <td>
                              <Badge color={getStatusBadgeColor(record.status)}>
                                {record.status || 'Unknown'}
                              </Badge>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>
                              <strong>{record.rfq_number || '-'}</strong>
                            </td>
                            <td>{record.title || '-'}</td>
                            <td>{record.project_name || '-'}</td>
                            <td>{record.vendor_name || 'Not Assigned'}</td>
                            {type === 'financial_summary_all' ? (
                              <>
                                <td>{formatCurrency(record.budget || 0)}</td>
                                <td>{formatCurrency(record.final_amount || 0)}</td>
                                <td>{formatCurrency(record.paid_amount || 0)}</td>
                                <td>{formatCurrency(record.pending_amount || 0)}</td>
                              </>
                            ) : (type === 'total_budget' || type === 'total_final_amount') ? (
                              <>
                                <td>{formatCurrency(record.budget || 0)}</td>
                                <td>{formatCurrency(record.final_amount || 0)}</td>
                              </>
                            ) : (
                              <td>{formatCurrency(record.amount || 0)}</td>
                            )}
                            <td>
                              <Badge color={getStatusBadgeColor(getStatusValue(record))}>
                                {getStatusValue(record)}
                              </Badge>
                            </td>
                        {type === 'financial_summary_all' ? (
                          <>
                            <td style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>{formatDate(record.created_at)}</td>
                            <td style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>
                              {record.estimated_date ? (
                                <small className="text-info">
                                  <i className="bx bx-calendar me-1"></i>
                                  {record.estimated_date}
                                </small>
                              ) : (
                                <small className="text-muted">-</small>
                              )}
                            </td>
                            <td style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>
                              {(() => {
                                const status = getStatusValue(record)?.toLowerCase() || '';
                                const completionDate = record.completion_date;
                                const hasValidCompletionDate = completionDate &&
                                  completionDate !== '' &&
                                  completionDate !== null &&
                                  completionDate !== '01-01-70' &&
                                  completionDate !== '00-00-00' &&
                                  !completionDate.startsWith('01-01-70');

                                console.log('Completion Date Debug:', {
                                  rfq_number: record.rfq_number,
                                  status: status,
                                  raw_completion_date: completionDate,
                                  hasValidCompletionDate: hasValidCompletionDate,
                                  isClosed: status === 'closed'
                                });

                                if (status === 'closed' && hasValidCompletionDate) {
                                  return (
                                    <small className="text-success">
                                      <i className="bx bx-check-circle me-1"></i>
                                      {completionDate}
                                    </small>
                                  );
                                } else if (status === 'closed') {
                                  return (
                                    <small className="text-warning">
                                      <i className="bx bx-time me-1"></i>
                                      Not Set
                                    </small>
                                  );
                                } else {
                                  return <small className="text-muted">-</small>;
                                }
                              })()}
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>{formatDate(record.date || record.created_at)}</td>
                            <td style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>
                              {record.estimated_date ? (
                                <small className="text-info">
                                  <i className="bx bx-calendar me-1"></i>
                                  {record.estimated_date}
                                </small>
                              ) : (
                                <small className="text-muted">-</small>
                              )}
                            </td>
                            <td style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>
                              {(() => {
                                const status = getStatusValue(record)?.toLowerCase() || '';
                                const completionDate = record.completion_date;
                                const hasValidCompletionDate = completionDate &&
                                  completionDate !== '' &&
                                  completionDate !== null &&
                                  completionDate !== '01-01-70' &&
                                  completionDate !== '00-00-00' &&
                                  !completionDate.startsWith('01-01-70');

                                console.log('Completion Date Debug (Other Types):', {
                                  rfq_number: record.rfq_number,
                                  status: status,
                                  raw_completion_date: completionDate,
                                  hasValidCompletionDate: hasValidCompletionDate,
                                  isClosed: status === 'closed'
                                });

                                if (status === 'closed' && hasValidCompletionDate) {
                                  return (
                                    <small className="text-success">
                                      <i className="bx bx-check-circle me-1"></i>
                                      {completionDate}
                                    </small>
                                  );
                                } else if (status === 'closed') {
                                  return (
                                    <small className="text-warning">
                                      <i className="bx bx-time me-1"></i>
                                      Not Set
                                    </small>
                                  );
                                } else {
                                  return <small className="text-muted">-</small>;
                                }
                              })()}
                            </td>
                          </>
                        )}
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-4">
                <i className="ri-money-dollar-circle-line display-4 text-muted"></i>
                <h5 className="mt-3">No Financial Records Found</h5>
                <p className="text-muted">
                  {searchTerm ? 'Try adjusting your search criteria.' : 'No records match the current filters.'}
                </p>
              </div>
            )}
          </>
        )}

        {/* Pagination - Always show when there's data */}
        {!loading && !error && data && data.length > 0 && (
          <Row className="mt-3">
            <Col className="d-flex justify-content-between align-items-center">
              <div className="text-muted">
                {pagination ? (
                  <>
                    Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, pagination.total)} of {pagination.total} records
                  </>
                ) : (
                  <>Showing {data.length} records</>
                )}
              </div>
              {pagination && pagination.total_pages > 1 && (
                <div className="d-flex align-items-center">
                  <Button
                    color="outline-primary"
                    size="sm"
                    onClick={() => loadData(currentPage - 1)}
                    disabled={currentPage <= 1 || loading}
                    className="me-2"
                  >
                    <i className="bx bx-chevron-left"></i>
                    Previous
                  </Button>
                  <span className="mx-3">
                    Page {currentPage} of {pagination.total_pages}
                  </span>
                  <Button
                    color="outline-primary"
                    size="sm"
                    onClick={() => loadData(currentPage + 1)}
                    disabled={currentPage >= pagination.total_pages || loading}
                  >
                    Next
                    <i className="bx bx-chevron-right"></i>
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        )}


      </ModalBody>
    </Modal>
  )
}

export default FinancialDetailModal
